/**
 * The Cross-check tab: extracted figures against the issuer's own announcement.
 *
 * The same figure exists twice — once as this pipeline read it off the filing
 * PDF, once as the issuer typed it into its Saudi Exchange results
 * announcement. The two share no failure modes, so agreement corroborates the
 * extraction and disagreement is a lead.
 *
 * Findings sit under a vertical rail of *causes* rather than in one list,
 * because the causes call for entirely different work: a misread numeral is a
 * fix in the extraction tier, an issuer who typed riyals into a thousands form
 * is not our defect at all, and an attributable-versus-total gap is not a
 * defect in the first place. One flat list of "131 mismatches" is what makes a
 * validation screen get ignored.
 *
 * Every row on this screen — findings and agreements alike — cites both ends:
 * a link into the viewer at the exact document and page the number was read
 * from, and the announcement it was compared against, in both languages. A
 * finding nobody can open is only an assertion.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { Link } from 'wouter'

import type { CrosscheckResponse, CrosscheckRow } from '@server/sql/announcements.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, money, percent0 } from '~/i18n/format.ts'
import { Badge, Skeleton, type State } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Disclosure } from '~/components/ui/disclosure.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Raw, Untranslated } from '~/components/ui/raw.tsx'
import { cn } from '~/lib/cn.ts'

/** Which side of the fence a cause falls on — it decides the colour. */
const CAUSE_GROUP: Record<string, 'parser' | 'issuer' | 'benign'> = {
  parse_digit: 'parser', parse_scale: 'parser', parse_sign: 'parser',
  parse_column: 'parser', parse_other: 'parser',
  announcement_arithmetic: 'issuer', announcement_unit: 'issuer',
  announcement_value: 'issuer',
  scope_attributable: 'benign', scope_restated: 'benign',
  concept_mismatch: 'benign', unresolved: 'benign',
}

const GROUP_STATE: Record<'parser' | 'issuer' | 'benign', State> = {
  parser: 'fail', issuer: 'accent', benign: 'neutral',
}

export function CompanyCrosscheck({ symbol }: { symbol: string }) {
  const { t, locale } = useT()
  const [cause, setCause] = useState<string | null>(null)

  const { data, error, refetch } = useQuery({
    queryKey: keys.companyCrosscheck(symbol),
    queryFn: () => api<CrosscheckResponse>(
      `/api/companies/${encodeURIComponent(symbol)}/crosscheck`),
  })

  if (error) {
    const e = error as ApiError
    return <ErrorState title={t('common.error')} detail={e.message} requestId={e.requestId}
      onRetry={() => void refetch()} retryLabel={t('action.retry')} />
  }
  if (!data) return <Skeleton className="h-96 w-full" />
  if (data.summary.comparisons === 0)
    return <EmptyState title={t('crosscheck.none')}>{t('crosscheck.noneDetail')}</EmptyState>

  const s = data.summary
  const active = cause ?? data.causes[0]?.type ?? null
  const shown = active ? data.findings.filter((f) => f.errorType === active) : []

  return (
    <div className="space-y-6">
      {/* --- what the check found ------------------------------------- */}
      {/* Extraction accuracy leads, because it answers the question this
          screen exists for. Raw agreement sits beside it and is deliberately
          not the headline: two sources can disagree for entirely legitimate
          reasons, and 22 attributable-vs-total differences should not read
          as a parser that is wrong a quarter of the time. */}
      <section className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t('crosscheck.accuracy')}
              value={percent0(s.extractionAccuracy, locale)}
              hint={t('crosscheck.accuracyHint')}
              state={s.extractionAccuracy === null ? 'neutral'
                : s.extractionAccuracy >= 0.99 ? 'pass'
                : s.extractionAccuracy >= 0.95 ? 'warn' : 'fail'} />
        <Stat label={t('crosscheck.ourFaults')} value={count(s.parserFailures, locale)}
              hint={t('crosscheck.ourFaultsHint')}
              state={s.parserFailures > 0 ? 'fail' : 'pass'} />
        <Stat label={t('crosscheck.comparisons')} value={count(s.comparisons, locale)}
              hint={t('crosscheck.exactWithin', {
                exact: count(s.exact, locale), within: count(s.withinPrecision, locale) })} />
        <Stat label={t('crosscheck.agreement')}
              value={percent0(s.agreementRate, locale)}
              hint={t('crosscheck.agreementHint')} />
        <Stat label={t('crosscheck.notDefects')} value={count(s.benign, locale)}
              hint={t('crosscheck.notDefectsHint')} />
        <Stat label={t('crosscheck.issuerSide')} value={count(s.issuerFailures, locale)}
              hint={t('crosscheck.issuerSideHint')} />
      </section>

      {/* --- findings, under a rail of causes -------------------------- */}
      {data.causes.length === 0 ? (
        <EmptyState title={t('crosscheck.allAgree')} />
      ) : (
        <section>
          <h3 className="text-[length:var(--text-title)] font-semibold">{t('crosscheck.byCause')}</h3>
          <p className="mt-0.5 text-[length:var(--text-data)] text-[var(--text-muted)]">
            {t('crosscheck.byCauseHint')}
          </p>
          <div className="mt-3 flex flex-col gap-5 lg:flex-row">
            <nav className="lg:w-64 lg:shrink-0">
              <div className="sticky top-4 space-y-0.5 rounded-[var(--radius-panel)] border
                              border-[var(--border)] bg-[var(--bg-panel)] p-1.5">
                {data.causes.map((c) => {
                  const group = CAUSE_GROUP[c.type] ?? 'benign'
                  const on = c.type === active
                  return (
                    <button
                      key={c.type}
                      onClick={() => setCause(c.type)}
                      aria-current={on || undefined}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-[var(--radius-control)] px-2.5 py-2',
                        'text-start text-[length:var(--text-data)] transition-colors',
                        on ? 'bg-[var(--bg-raised)] font-medium text-[var(--text)]'
                           : 'text-[var(--text-muted)] hover:bg-[var(--bg-sunken)]',
                      )}
                    >
                      <Badge state={GROUP_STATE[group]} className="shrink-0">
                        {t(`crosscheck.group.${group}` as never)}
                      </Badge>
                      <span className="min-w-0 flex-1 truncate">
                        {t(`crosscheck.cause.${c.type}` as never)}
                      </span>
                      <span className="tnum shrink-0 font-[family-name:var(--font-mono)]
                                       text-[length:var(--text-micro)] text-[var(--text-muted)]">
                        {count(c.count, locale)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </nav>

            <div className="min-w-0 flex-1 space-y-4">
              {active && (
                <div className="rounded-[var(--radius-panel)] border border-[var(--border)]
                                bg-[var(--bg-sunken)] px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge state={GROUP_STATE[CAUSE_GROUP[active] ?? 'benign']}>
                      {t(`crosscheck.group.${CAUSE_GROUP[active] ?? 'benign'}` as never)}
                    </Badge>
                    <h4 className="text-[length:var(--text-title)] font-semibold">
                      {t(`crosscheck.cause.${active}` as never)}
                    </h4>
                    <span className="tnum text-[length:var(--text-data)] text-[var(--text-muted)]">
                      {count(shown.length, locale)}
                    </span>
                  </div>
                  <p className="mt-1 max-w-3xl text-[length:var(--text-data)] text-[var(--text-muted)]">
                    {t(`crosscheck.causeHint.${active}` as never)}
                  </p>
                </div>
              )}
              {shown.map((f) => <Finding key={f.id} row={f} symbol={symbol} />)}
            </div>
          </div>
        </section>
      )}

      {/* --- the agreements, cited the same way ------------------------ */}
      <Disclosure
        summary={t('crosscheck.agreements')}
        count={data.agreements.length}
        tone="strong"
      >
        <p className="mb-2 text-[length:var(--text-data)] text-[var(--text-muted)]">
          {t('crosscheck.agreementsHint')}
        </p>
        <Table>
          <thead>
            <Tr>
              <Th>{t('crosscheck.period')}</Th>
              <Th>{t('crosscheck.lineItem')}</Th>
              <Th>{t('crosscheck.grid')}</Th>
              <Th numeric>{t('crosscheck.extracted')}</Th>
              <Th numeric>{t('crosscheck.announced')}</Th>
              <Th numeric>{t('crosscheck.delta')}</Th>
              <Th>{t('crosscheck.source')}</Th>
              <Th>{t('crosscheck.announcement')}</Th>
            </Tr>
          </thead>
          <tbody>
            {data.agreements.map((r) => (
              <Tr key={r.id} interactive>
                <Td className="whitespace-nowrap">{r.period ?? '—'}</Td>
                <Td>{t(`crosscheck.concept.${r.concept}` as never)}</Td>
                <Td className="text-[var(--text-muted)]">
                  {r.grid ? t(`crosscheck.gridName.${r.grid}` as never) : '—'}
                </Td>
                <Td numeric>{money(r.extracted, locale)}</Td>
                <Td numeric>{money(r.announced, locale)}</Td>
                <Td numeric className={cn(Number(r.delta) === 0
                  ? 'text-[var(--pass)]' : 'text-[var(--text-muted)]')}>
                  {money(r.delta, locale)}
                </Td>
                <Td><SourceLink row={r} symbol={symbol} /></Td>
                <Td><AnnouncementLinks row={r} compact /></Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </Disclosure>
    </div>
  )
}

// --------------------------------------------------------------------------

function Stat({ label, value, hint, state = 'neutral' }: {
  label: string; value: string; hint?: string; state?: State
}) {
  const tone = state === 'pass' ? 'text-[var(--pass)]'
    : state === 'warn' ? 'text-[var(--warn)]'
    : state === 'fail' ? 'text-[var(--fail)]' : 'text-[var(--text)]'
  return (
    <div className="rounded-[var(--radius-panel)] border border-[var(--border)]
                    bg-[var(--bg-panel)] px-3 py-2.5">
      <div className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                      text-[var(--text-muted)]">{label}</div>
      <div className={cn('tnum mt-0.5 text-[length:var(--text-display)] font-semibold', tone)}>
        {value}
      </div>
      {hint && <div className="text-[length:var(--text-micro)] text-[var(--text-faint)]">{hint}</div>}
    </div>
  )
}

/**
 * The link into the viewer, at the page the figure was read from.
 *
 * This is the whole point of putting the check in the explorer rather than in
 * a standalone report: the page image the model was actually sent is two
 * clicks away, so "is the extraction wrong" stops being a judgement call.
 */
function SourceLink({ row, symbol }: { row: CrosscheckRow; symbol: string }) {
  const { t, v } = useT()
  const href = row.pageNo
    ? `/c/${symbol}/d/${row.documentId}?p=${row.pageNo}`
    : `/c/${symbol}/d/${row.documentId}`
  return (
    <Link href={href} className="link-inline whitespace-nowrap"
          title={t('crosscheck.openPage', { page: String(row.pageNo ?? '?') })}>
      <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-micro)]">
        {v('docType', row.docType ?? '') ?? row.docType}
        {row.fiscalYear ? ` ${row.fiscalYear}` : ''}
        {row.docPeriod ? `/${row.docPeriod}` : ''}
        {row.pageNo ? ` p${row.pageNo}` : ''}
      </span>
    </Link>
  )
}

/** Both language versions of the source announcement — the citation. */
function AnnouncementLinks({ row, compact }: { row: CrosscheckRow; compact?: boolean }) {
  const { t } = useT()
  if (!row.anUrlEn && !row.anUrlAr) return <span className="text-[var(--text-faint)]">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {row.anUrlEn && (
        <a href={row.anUrlEn} target="_blank" rel="noopener noreferrer"
           className="link-inline text-[length:var(--text-micro)]"
           title={row.anTitleEn ?? undefined}>
          EN<ExternalLink className="inline size-3 align-[-1px]" />
        </a>
      )}
      {row.anUrlAr && (
        <a href={row.anUrlAr} target="_blank" rel="noopener noreferrer"
           className="link-inline text-[length:var(--text-micro)]"
           title={row.anTitleAr ?? undefined}>
          AR<ExternalLink className="inline size-3 align-[-1px]" />
        </a>
      )}
      {!compact && row.anId && (
        <span className="tnum font-[family-name:var(--font-mono)]
                         text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {t('crosscheck.anId')} {row.anId}
        </span>
      )}
    </span>
  )
}

function Finding({ row, symbol }: { row: CrosscheckRow; symbol: string }) {
  const { t, locale } = useT()
  const group = CAUSE_GROUP[row.errorType] ?? 'benign'

  return (
    <article className="rounded-[var(--radius-panel)] border border-[var(--border)]
                        bg-[var(--bg-panel)] p-4">
      <header className="flex flex-wrap items-center gap-2">
        <Badge state={row.severity === 'error' ? 'fail' : 'warn'}>
          {row.severity === 'error' ? t('crosscheck.error') : t('crosscheck.warning')}
        </Badge>
        <h4 className="text-[length:var(--text-title)] font-semibold">
          {t(`crosscheck.concept.${row.concept}` as never)}
        </h4>
        <Badge state="accent">{row.period ?? '—'}</Badge>
        <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">
          {row.grid ? t(`crosscheck.gridName.${row.grid}` as never) : ''}
        </span>
        <span className="ms-auto"><AnnouncementLinks row={row} /></span>
      </header>

      {/* the two numbers, side by side */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Side
          title={t('crosscheck.extracted')}
          value={money(row.extracted, locale)}
          sub={t('crosscheck.printedScale', {
            printed: row.scaleApplied === null ? '—' : String(row.scaleApplied),
          })}
          label={row.labelRaw}
          footer={<SourceLink row={row} symbol={symbol} />}
        />
        <Side
          title={t('crosscheck.announced')}
          value={money(row.announced, locale)}
          sub={t('crosscheck.printedUnit', {
            printed: row.announcedPrinted ?? '—',
            unit: row.unitsMultiplier === null ? '—' : String(row.unitsMultiplier),
          })}
          label={row.announcedLabelEn}
        />
      </div>

      {/* the gap */}
      <div className="tnum mt-3 flex flex-wrap items-center gap-x-6 gap-y-1
                      rounded-[var(--radius-control)] bg-[var(--bg-sunken)] px-3 py-2">
        <Metric label={t('crosscheck.delta')} value={money(row.delta, locale)} strong />
        <Metric label={t('crosscheck.tolerance')} value={`± ${money(row.tolerance, locale)}`} />
        {row.classification && (
          <Metric label={t('crosscheck.shape')} value={row.classification} mono />
        )}
        {row.matchedBy && (
          <Metric label={t('crosscheck.matchedBy')}
                  value={t(`crosscheck.matched.${row.matchedBy}` as never)} />
        )}
      </div>

      {/* Proved, not judged: the announcement's own quarters settle it, and
          no judge call was spent. Showing the implied figure is the proof. */}
      {row.errorType === 'announcement_arithmetic' && (
        <div className="mt-3 rounded-[var(--radius-control)] border border-[var(--border)]
                        bg-[var(--bg-sunken)] px-3 py-2">
          <span className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                           text-[var(--text-muted)]">{t('crosscheck.settled')}</span>
          {row.selfCheckImplied && (
            <div className="tnum mt-0.5 text-[length:var(--text-data)]">
              {t('crosscheck.ownQuarters')}{' '}
              <span className="font-[family-name:var(--font-mono)] font-semibold">
                {money(row.selfCheckImplied, locale)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* what the judge made of it */}
      {row.judge?.verdict && (
        <div className="mt-3 rounded-[var(--radius-control)] border border-[var(--border)]
                        bg-[var(--bg-sunken)] px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                             text-[var(--text-muted)]">{t('crosscheck.judge')}</span>
            <Badge state={GROUP_STATE[group]}>
              {t(`crosscheck.verdict.${row.judge.verdict}` as never)}
            </Badge>
            {row.judge.confidence && (
              <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
                {t('crosscheck.confidence')}: {row.judge.confidence}
              </span>
            )}
          </div>
          {row.judge.reasoning && (
            <p className="mt-1 text-[length:var(--text-data)]">{row.judge.reasoning}</p>
          )}
          {row.judge.whereToLook && (
            <p className="mt-1 text-[length:var(--text-data)] text-[var(--text-muted)]">
              <span className="font-medium">{t('crosscheck.whereToLook')}: </span>
              {row.judge.whereToLook}
            </p>
          )}
        </div>
      )}

      <Disclosure summary={t('crosscheck.evidence')} className="mt-3">
        <dl className="grid gap-x-8 gap-y-0 sm:grid-cols-2">
          <Kv k={t('crosscheck.factId')} v={row.factId} mono />
          <Kv k={t('crosscheck.anId')} v={row.anId} mono />
          <Kv k={t('crosscheck.documentId')} v={row.documentId} mono />
          <Kv k={t('crosscheck.form')} v={row.anFormLabel} />
          <Kv k={t('crosscheck.page')} v={row.pageNo} mono />
          <Kv k={t('crosscheck.published')} v={row.anPublishedAt} />
          <Kv k={t('crosscheck.locale')} v={row.locale} />
          <Kv k={t('crosscheck.columnRole')} v={row.columnRole} />
          <Kv k={t('crosscheck.rowLabel')} v={row.labelRaw} raw />
          <Kv k={t('crosscheck.columnLabel')} v={row.columnLabel} raw />
          <Kv k={t('crosscheck.periodType')} v={row.periodType} />
          {row.matchesPriorYear && (
            <Kv k={t('crosscheck.priorYearMatch')} v={t('crosscheck.yes')} />
          )}
        </dl>
      </Disclosure>
    </article>
  )
}

function Side({ title, value, sub, label, footer }: {
  title: string; value: string; sub: string; label: string | null; footer?: React.ReactNode
}) {
  return (
    <div className="rounded-[var(--radius-control)] border border-[var(--border)]
                    bg-[var(--bg-base)] px-3 py-2.5">
      <div className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                      text-[var(--text-muted)]">{title}</div>
      <div className="tnum mt-0.5 font-[family-name:var(--font-mono)]
                      text-[length:var(--text-display)] font-semibold">{value}</div>
      <div className="text-[length:var(--text-micro)] text-[var(--text-faint)]">{sub}</div>
      {label && (
        <Raw className="mt-1.5 block rounded bg-[var(--bg-sunken)] px-1.5 py-1
                        text-[length:var(--text-data)]">{label}</Raw>
      )}
      {footer && <div className="mt-1.5">{footer}</div>}
    </div>
  )
}

function Metric({ label, value, strong, mono }: {
  label: string; value: string; strong?: boolean; mono?: boolean
}) {
  return (
    <div>
      <div className="text-[length:var(--text-micro)] uppercase tracking-[0.04em]
                      text-[var(--text-muted)]">{label}</div>
      <div className={cn('text-[length:var(--text-data)]',
        strong && 'font-semibold',
        mono && 'font-[family-name:var(--font-mono)]')}>{value}</div>
    </div>
  )
}

function Kv({ k, v, mono, raw }: {
  k: string; v: unknown; mono?: boolean; raw?: boolean
}) {
  if (v === null || v === undefined || v === '') return null
  return (
    <div className="flex gap-3 border-b border-[var(--border)] py-1 last:border-0">
      <dt className="w-40 shrink-0 text-[length:var(--text-micro)] text-[var(--text-muted)]">{k}</dt>
      <dd className={cn('min-w-0 break-words text-[length:var(--text-data)]',
        mono && 'tnum font-[family-name:var(--font-mono)]')}>
        {raw ? <Raw>{String(v)}</Raw> : <Untranslated>{String(v)}</Untranslated>}
      </dd>
    </div>
  )
}
